import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import {
  ChevronDown, ChevronRight, Info, Monitor, Fuel, HeartPulse, Ship, Factory, Landmark, Plane, Truck, GraduationCap, Sprout, Hotel, Train, Plus, Trash2,
} from 'lucide-react';
import {
  JOB_FAMILY_INTRO,
  getJobFamilyDomains,
  JOB_FAMILY_MASTER_UPDATED_EVENT,
  addCustomDomain,
  addCustomFamily,
  addCustomRole,
  deleteDomain,
  getRoleKey,
  parseRoleKey,
  findRoleByKey,
  applyLastCreatedRoleSelection,
  getTotalWeight,
  getRoleSkills,
  saveStoredRoleSkills,
  generateAiRoleSkillWeights,
  isJobFamilyAiEnabled,
} from '../../data/jobFamilyMaster';
import { getDefaultSkillWeightagesForTitle } from '../../utils/stackRankingScores';

const FEUJI_GRAD = 'linear-gradient(135deg,#0041C2,#1A5FD4)';
const FEUJI_GRAD_HOVER = 'linear-gradient(135deg,#003399,#0041C2)';

function primaryBtnStyle(extra = {}) {
  return {
    background: FEUJI_GRAD,
    border: 'none',
    color: '#fff',
    fontWeight: 600,
    transition: 'all 0.18s ease',
    boxShadow: '0 2px 8px rgba(0,65,194,0.25)',
    ...extra,
  };
}

function primaryBtnHoverHandlers(enabled = true) {
  if (!enabled) return {};
  return {
    onMouseEnter: (e) => {
      e.currentTarget.style.background = FEUJI_GRAD_HOVER;
      e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,65,194,0.4)';
      e.currentTarget.style.transform = 'translateY(-1px)';
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.background = FEUJI_GRAD;
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,65,194,0.25)';
      e.currentTarget.style.transform = 'translateY(0)';
    },
  };
}

function sanitizeWeightage(value) {
  return Math.max(0, Math.min(100, Number(value) || 0));
}

const DOMAIN_ICONS = {
  it: Monitor,
  petroleum: Fuel,
  healthcare: HeartPulse,
  marine: Ship,
  manufacturing: Factory,
  banking: Landmark,
  aviation: Plane,
  logistics: Truck,
  education: GraduationCap,
  agriculture: Sprout,
  'hotel-management': Hotel,
  railways: Train,
};

function useJobFamilyDomains() {
  const [domains, setDomains] = useState(() => getJobFamilyDomains());

  useEffect(() => {
    const refresh = () => setDomains(getJobFamilyDomains());
    window.addEventListener(JOB_FAMILY_MASTER_UPDATED_EVENT, refresh);
    return () => window.removeEventListener(JOB_FAMILY_MASTER_UPDATED_EVENT, refresh);
  }, []);

  return domains;
}

function CreateItemModal({ title, fields, onClose, onSubmit, submitting = false, submitLabel = 'Create' }) {
  const [values, setValues] = useState(() => fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {}));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    onSubmit(values);
  };

  return (
    <div className="settings-jf-modal-backdrop" onClick={submitting ? undefined : onClose} role="presentation">
      <div className="settings-jf-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="settings-jf-modal-header">
          <h6 className="settings-jf-modal-title">{title}</h6>
          <button type="button" className="settings-jf-modal-close" onClick={onClose} disabled={submitting} aria-label="Close">×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="settings-jf-modal-body">
            {fields.map((field) => (
              <div key={field.name} className="settings-jf-modal-field">
                <label htmlFor={`jf-create-${field.name}`}>{field.label}</label>
                {field.multiline ? (
                  <textarea
                    id={`jf-create-${field.name}`}
                    placeholder={field.placeholder}
                    value={values[field.name]}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    rows={3}
                    disabled={submitting}
                  />
                ) : (
                  <input
                    id={`jf-create-${field.name}`}
                    type="text"
                    placeholder={field.placeholder}
                    value={values[field.name]}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    autoFocus={field.autoFocus}
                    disabled={submitting}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="settings-jf-modal-footer">
            <button type="button" className="settings-jf-modal-cancel" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="settings-jf-modal-submit" disabled={submitting}>
              {submitting ? 'Generating with AI…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ADD_MENU_Z_INDEX = 10050;

function AddMenuButton({ label, options, small = false, align = 'left' }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (!btnRef.current) return;

    const rect = btnRef.current.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, 160);
    const left = align === 'right'
      ? Math.max(8, rect.right - menuWidth)
      : rect.left;

    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left,
      minWidth: menuWidth,
      zIndex: ADD_MENU_Z_INDEX,
    });
  }, [align]);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();

    const handleClickOutside = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const handleReposition = () => updateMenuPosition();

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updateMenuPosition]);

  const handleToggle = () => {
    setOpen((prev) => {
      if (!prev) updateMenuPosition();
      return !prev;
    });
  };

  const handleOptionClick = (onClick) => {
    setOpen(false);
    onClick();
  };

  return (
    <div className="settings-jf-add-menu">
      <button
        ref={btnRef}
        type="button"
        className={`settings-jf-add-btn${small ? ' settings-jf-add-btn--sm' : ''}`}
        onClick={handleToggle}
        aria-expanded={open}
        aria-haspopup="menu"
        {...primaryBtnHoverHandlers(true)}
        style={primaryBtnStyle({
          borderRadius: 6,
          padding: small ? '4px 10px' : '6px 14px',
          fontSize: small ? '0.7rem' : '0.75rem',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
        })}
      >
        <Plus size={small ? 12 : 14} strokeWidth={2.5} aria-hidden="true" />
        {label}
        <ChevronDown
          size={small ? 12 : 14}
          strokeWidth={2.5}
          aria-hidden="true"
          className={`settings-jf-add-menu-chevron${open ? ' settings-jf-add-menu-chevron--open' : ''}`}
        />
      </button>

      {open && menuStyle && createPortal(
        <div
          ref={menuRef}
          className="settings-jf-add-menu-dropdown settings-jf-add-menu-dropdown--portal"
          style={menuStyle}
          role="menu"
        >
          {options.map((opt) => (
            <button
              key={opt.label}
              type="button"
              className="settings-jf-add-menu-item"
              role="menuitem"
              onClick={() => handleOptionClick(opt.onClick)}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function resolveTripleIds(roleKey, lockDomainId) {
  if (roleKey) {
    const { domain, family, role } = findRoleByKey(roleKey);
    if (domain && family && role) {
      return { domainId: domain.id, familyId: family.id, roleId: role.id };
    }
  }

  return {
    domainId: lockDomainId || '',
    familyId: '',
    roleId: '',
  };
}

export function JobFamilyTripleSelect({
  roleKey = '',
  onRoleKeyChange,
  readOnly = false,
  lockDomainId = null,
  hideDomainWhenLocked = true,
  idPrefix = 'jf-triple',
  preview = null,
}) {
  const domains = useJobFamilyDomains();
  const ids = useMemo(() => resolveTripleIds(roleKey, lockDomainId), [roleKey, lockDomainId]);
  const hasResolvedRole = Boolean(roleKey && findRoleByKey(roleKey).role);
  const previewActive = Boolean(!hasResolvedRole && preview?.sectorId && preview?.familyId);

  const domain = domains.find((d) => d.id === (ids.domainId || preview?.sectorId));
  const families = domain?.families || [];
  const family = families.find((f) => f.id === (ids.familyId || preview?.familyId)) || null;
  const roles = family?.roles || [];
  const role = roles.find((r) => r.id === ids.roleId) || null;

  const effectiveDomainId = lockDomainId || ids.domainId || preview?.sectorId || '';
  const effectiveFamilyId = ids.familyId || preview?.familyId || '';
  const effectiveRoleId = role?.id || '';
  const displayDomain = domains.find((d) => d.id === effectiveDomainId) || domain;
  const showDomain = !(hideDomainWhenLocked && lockDomainId);
  const hasProfile = hasResolvedRole || previewActive;
  const sectorLabel = 'Sector';
  const domainLabel = 'Domain';
  const roleLabel = 'Designation';

  const emit = (dId, fId, rId) => {
    if (!onRoleKeyChange) return;
    const d = domains.find((x) => x.id === dId);
    const f = d?.families.find((x) => x.id === fId);
    const r = f?.roles.find((x) => x.id === rId);
    if (d && f && r) onRoleKeyChange(getRoleKey(d.id, f.id, r.id));
  };

  const renderBox = (label, content) => (
    <div className="settings-jf-triple-box">
      <span className="settings-jf-triple-label">{label}</span>
      <div className="settings-jf-triple-field">{content}</div>
    </div>
  );

  const domainBox = showDomain ? renderBox(
    sectorLabel,
    readOnly ? (
      <div className="settings-jf-triple-value">{displayDomain?.name || '—'}</div>
    ) : (
      <select
        id={`${idPrefix}-domain`}
        className="settings-jf-triple-select"
        value={effectiveDomainId}
        onChange={(e) => {
          const dId = e.target.value;
          if (!dId) return;
          const d = domains.find((x) => x.id === dId);
          const f = d?.families[0];
          emit(dId, f?.id, f?.roles[0]?.id);
        }}
      >
        {!hasProfile && <option value="">Select sector</option>}
        {domains.map((d) => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
      </select>
    )
  ) : null;

  const familyBox = renderBox(
    domainLabel,
    readOnly ? (
      <div className="settings-jf-triple-value">{family?.name || '—'}</div>
    ) : (
      <select
        id={`${idPrefix}-family`}
        className="settings-jf-triple-select"
        value={effectiveFamilyId}
        disabled={!effectiveDomainId}
        onChange={(e) => {
          const fId = e.target.value;
          if (!fId) return;
          const f = families.find((x) => x.id === fId);
          emit(effectiveDomainId, fId, f?.roles[0]?.id);
        }}
      >
        {!hasProfile && <option value="">Select domain</option>}
        {families.map((f) => (
          <option key={f.id} value={f.id}>{f.name}</option>
        ))}
      </select>
    )
  );

  const roleBox = renderBox(
    roleLabel,
    readOnly ? (
      <div className="settings-jf-triple-value">{role?.title || preview?.pendingRoleTitle || '—'}</div>
    ) : previewActive ? (
      <div className="settings-jf-triple-value settings-jf-triple-value--pending" title="This designation will be created in Job Family when you save the position">
        {preview.pendingRoleTitle}
        <span className="settings-jf-triple-pending-tag">new</span>
      </div>
    ) : (
      <select
        id={`${idPrefix}-role`}
        className="settings-jf-triple-select"
        value={effectiveRoleId}
        disabled={!effectiveFamilyId}
        onChange={(e) => {
          if (!e.target.value) return;
          emit(effectiveDomainId, effectiveFamilyId, e.target.value);
        }}
      >
        {!hasProfile && <option value="">Select role</option>}
        {roles.map((r) => (
          <option key={r.id} value={r.id}>{r.title}</option>
        ))}
      </select>
    )
  );

  const steps = [domainBox, familyBox, roleBox].filter(Boolean);

  return (
    <div className="settings-jf-triple settings-jf-triple--flow">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <ChevronRight className="settings-jf-triple-sep" size={14} aria-hidden="true" />
          )}
          {step}
        </React.Fragment>
      ))}
    </div>
  );
}

function EditableSkillEditor({ roleKey }) {
  const [skills, setSkills] = useState([]);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillWeight, setNewSkillWeight] = useState(0);
  const [editingSkills, setEditingSkills] = useState(false);
  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [invalidSkillIndexes, setInvalidSkillIndexes] = useState({});

  useEffect(() => {
    if (!roleKey) {
      setSkills([]);
      setEditingSkills(false);
      setInvalidSkillIndexes({});
      return;
    }
    setSkills(getRoleSkills(roleKey).map((s) => ({ ...s })));
    setNewSkillName('');
    setNewSkillWeight(0);
    setEditingSkills(false);
    setInvalidSkillIndexes({});
  }, [roleKey]);

  const total = useMemo(() => getTotalWeight(skills), [skills]);
  const isValidTotal = total === 100;
  const hasInvalidWeights = skills.some((s) => !s.skill?.trim() || Number(s.weight) <= 0);
  const hasAnyInvalidSkillValues = Object.values(invalidSkillIndexes).some(Boolean)
    || skills.some((s) => Number(s.weight) <= 0);

  const updateSkill = (index, field, value) => {
    setSkills((prev) => prev.map((s, i) => {
      if (i !== index) return s;
      if (field === 'weight') {
        const n = sanitizeWeightage(value);
        setInvalidSkillIndexes((prevInvalid) => ({ ...prevInvalid, [index]: n <= 0 }));
        return { ...s, weight: n };
      }
      return { ...s, skill: value };
    }));
  };

  const removeSkill = (index) => {
    const skillName = skills[index]?.skill;
    if (!window.confirm(`Are you sure you want to delete "${skillName}"?`)) return;
    setSkills((prev) => prev.filter((_, i) => i !== index));
    setInvalidSkillIndexes({});
  };

  const handleAddSkill = () => {
    const name = newSkillName.trim();
    const weight = sanitizeWeightage(newSkillWeight);
    if (!name || weight <= 0) {
      toast.error('Please enter valid skill name and weightage');
      return;
    }
    if (skills.some((s) => s.skill.toLowerCase() === name.toLowerCase())) {
      toast.error('Skill already exists');
      return;
    }
    setSkills((prev) => [...prev, { skill: name, weight }]);
    setNewSkillName('');
    setNewSkillWeight(0);
    setShowAddSkillModal(false);
    toast.success('Skill added successfully!');
  };

  const handleSave = () => {
    if (hasAnyInvalidSkillValues) {
      toast.error('Skill weightage must be a positive value');
      return;
    }
    if (hasInvalidWeights) {
      toast.error('Each skill needs a name and a positive weight.');
      return;
    }
    if (!isValidTotal) {
      toast.error('Total weightage must equal 100%');
      return;
    }
    saveStoredRoleSkills(roleKey, skills);
    window.dispatchEvent(new CustomEvent('reachout:job-family-skills-updated', { detail: { roleKey } }));
    setEditingSkills(false);
    setInvalidSkillIndexes({});
    toast.success('Skills saved successfully!');
  };

  const handleCancelEdit = () => {
    setSkills(getRoleSkills(roleKey).map((s) => ({ ...s })));
    setNewSkillName('');
    setNewSkillWeight(0);
    setEditingSkills(false);
    setInvalidSkillIndexes({});
  };

  if (!roleKey) return null;

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
          <h6 style={{ fontWeight: 700, fontSize: 16, margin: 0, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="fas fa-balance-scale" style={{ fontSize: 14, color: '#1A5FD4' }} aria-hidden="true" />
            Skill Weightage (Total: {total}%)
            {!isValidTotal && <span style={{ color: '#dc3545', fontSize: 12, marginLeft: 4 }}>⚠️ Must equal 100%</span>}
          </h6>
          <div>
            {!editingSkills ? (
              <button
                type="button"
                onClick={() => setEditingSkills(true)}
                {...primaryBtnHoverHandlers(true)}
                style={primaryBtnStyle({ borderRadius: 4, padding: '4px 8px', fontSize: '0.7rem', cursor: 'pointer' })}
              >
                <i className="fas fa-edit" style={{ fontSize: '0.6rem', marginRight: 4 }} aria-hidden="true" />
                Edit Skills
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isValidTotal || hasAnyInvalidSkillValues || hasInvalidWeights}
                  style={{ borderRadius: 4, padding: '4px 8px', fontWeight: 600, fontSize: '0.7rem', background: '#28a745', border: 'none', color: 'white', cursor: 'pointer', opacity: (!isValidTotal || hasAnyInvalidSkillValues || hasInvalidWeights) ? 0.6 : 1 }}
                >
                  <i className="fas fa-save" style={{ fontSize: '0.6rem', marginRight: 4 }} aria-hidden="true" />
                  Save
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  style={{ borderRadius: 4, padding: '4px 8px', fontWeight: 600, fontSize: '0.7rem', background: 'transparent', border: '1px solid var(--ds-border)', color: 'var(--ds-text)', cursor: 'pointer' }}
                >
                  <i className="fas fa-times" style={{ fontSize: '0.6rem', marginRight: 4 }} aria-hidden="true" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ background: 'var(--ds-surface)', border: '1px solid var(--ds-border)', borderRadius: 8, padding: 8 }}>
          {skills.length === 0 ? (
            <p style={{ margin: 0, padding: 8, color: 'var(--ds-muted)', fontSize: 13 }}>
              No skill weights found for this role.
            </p>
          ) : (
            skills.map((sw, index) => (
              <div
                key={`${sw.skill}-${index}`}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: 4, border: '1px solid var(--ds-border)', borderRadius: 8, background: 'var(--ds-card)', minHeight: 30, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
              >
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  {editingSkills ? (
                    <input
                      type="text"
                      style={{ minWidth: 150, borderRadius: 4, border: '1px solid var(--ds-border)', padding: '4px 8px', fontSize: '0.7rem', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', marginRight: 8 }}
                      value={sw.skill}
                      onChange={(e) => updateSkill(index, 'skill', e.target.value)}
                    />
                  ) : (
                    <span style={{ fontSize: 14, color: 'var(--ds-text)', fontWeight: 500 }}>{sw.skill}:</span>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {editingSkills ? (
                    <input
                      type="number"
                      style={{ width: 50, textAlign: 'center', borderRadius: 4, border: invalidSkillIndexes[index] ? '1px solid #dc3545' : '1px solid var(--ds-border)', padding: '2px 4px', fontSize: '0.7rem', marginRight: 4, background: 'var(--ds-input-bg)', color: 'var(--ds-text)' }}
                      value={sw.weight}
                      onChange={(e) => updateSkill(index, 'weight', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'Minus') {
                          e.preventDefault();
                          setInvalidSkillIndexes((prev) => ({ ...prev, [index]: true }));
                        }
                      }}
                      min="0"
                      max="100"
                    />
                  ) : (
                    <span style={{ fontSize: 14, minWidth: 30, textAlign: 'center', color: 'rgb(89,144,225)', fontWeight: 700, marginRight: 4 }}>
                      {sw.weight}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--ds-muted)', fontWeight: 500, marginRight: 4 }}>%</span>

                  {editingSkills && (
                    <button
                      type="button"
                      onClick={() => removeSkill(index)}
                      style={{ borderRadius: 4, padding: '2px 4px', fontSize: '0.7rem', border: '1px solid #dc3545', color: '#dc3545', background: 'transparent', cursor: 'pointer' }}
                    >
                      <i className="fas fa-trash" style={{ fontSize: '0.6rem' }} aria-hidden="true" />
                    </button>
                  )}
                  {editingSkills && invalidSkillIndexes[index] && (
                    <span style={{ fontSize: 10, color: '#dc3545', marginLeft: 4 }}>Must be positive</span>
                  )}
                </div>
              </div>
            ))
          )}

          {editingSkills && hasAnyInvalidSkillValues && (
            <div style={{ color: '#dc3545', fontSize: 12, marginTop: 4 }}>Skill weightage must be a positive value</div>
          )}

          {editingSkills && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setShowAddSkillModal(true)}
                style={{ borderRadius: 4, padding: '4px 8px', fontWeight: 600, fontSize: '0.7rem', background: '#28a745', border: 'none', color: 'white', cursor: 'pointer' }}
              >
                <i className="fas fa-plus" style={{ fontSize: '0.6rem', marginRight: 4 }} aria-hidden="true" />
                Add New Skill
              </button>
            </div>
          )}
        </div>
      </div>

      {showAddSkillModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--ds-card)', borderRadius: 12, width: '90%', maxWidth: 400, boxShadow: 'var(--ds-shadow-md)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--ds-text)', fontSize: 14 }}>Add New Skill</h6>
              <button type="button" onClick={() => setShowAddSkillModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, color: 'var(--ds-text)', display: 'block', marginBottom: 4 }}>Skill Name</label>
                <input type="text" placeholder="Enter skill name" value={newSkillName} onChange={(e) => setNewSkillName(e.target.value)} style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, color: 'var(--ds-text)', display: 'block', marginBottom: 4 }}>Weightage (%)</label>
                <input type="number" placeholder="Enter weightage" value={newSkillWeight} onChange={(e) => setNewSkillWeight(sanitizeWeightage(e.target.value))} onKeyDown={(e) => { if (e.key === '-' || e.key === 'Minus') e.preventDefault(); }} min="0" max="100" style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => setShowAddSkillModal(false)} style={{ borderRadius: 6, padding: '6px 14px', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button
                type="button"
                onClick={handleAddSkill}
                {...primaryBtnHoverHandlers(true)}
                style={primaryBtnStyle({ borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 })}
              >
                Add Skill
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function JobFamilyInfoButton() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);

  const showPopover = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpen(true);
  };

  const hidePopover = () => {
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <div
      className="settings-jf-info-wrap"
      ref={wrapRef}
      onMouseEnter={showPopover}
      onMouseLeave={hidePopover}
      onFocus={showPopover}
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget)) hidePopover();
      }}
    >
      <button
        type="button"
        className={`settings-jf-info-btn${open ? ' settings-jf-info-btn--active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="About Job Family"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Info size={18} strokeWidth={2.25} aria-hidden="true" />
      </button>

      {open && (
        <div className="settings-jf-info-popover" role="dialog" aria-label="Job Family description">
          <p className="settings-jf-intro-text">{JOB_FAMILY_INTRO.summary}</p>
          <ul className="settings-jf-intro-list">
            {JOB_FAMILY_INTRO.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function JobFamilyPanel() {
  const domains = useJobFamilyDomains();
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [createModal, setCreateModal] = useState(null);
  const [generatingRoleSkills, setGeneratingRoleSkills] = useState(false);

  useEffect(() => {
    const onMasterUpdated = (event) => {
      const roleKey = event?.detail?.roleKey;
      if (roleKey && findRoleByKey(roleKey).role) {
        const { domainId, familyId, roleId } = parseRoleKey(roleKey);
        setExpandedDomain(domainId);
        setSelectedFamilyId(familyId);
        setSelectedRoleId(roleId);
      }
    };
    window.addEventListener(JOB_FAMILY_MASTER_UPDATED_EVENT, onMasterUpdated);
    return () => window.removeEventListener(JOB_FAMILY_MASTER_UPDATED_EVENT, onMasterUpdated);
  }, []);

  useEffect(() => {
    applyLastCreatedRoleSelection({
      expandDomain: setExpandedDomain,
      selectFamily: setSelectedFamilyId,
      selectRole: setSelectedRoleId,
    });
  }, []);

  const activeDomain = useMemo(
    () => domains.find((d) => d.id === expandedDomain) || null,
    [domains, expandedDomain]
  );

  const activeFamily = useMemo(
    () => activeDomain?.families.find((f) => f.id === selectedFamilyId) || null,
    [activeDomain, selectedFamilyId]
  );

  const activeRole = useMemo(
    () => activeFamily?.roles.find((r) => r.id === selectedRoleId) || null,
    [activeFamily, selectedRoleId]
  );

  const activeRoleKey = activeDomain && activeFamily && activeRole
    ? getRoleKey(activeDomain.id, activeFamily.id, activeRole.id)
    : '';

  const toggleDomain = (domainId) => {
    setExpandedDomain((prev) => {
      if (prev === domainId) return null;
      const domain = domains.find((d) => d.id === domainId);
      const firstFamily = domain?.families[0];
      const firstRole = firstFamily?.roles[0];
      setSelectedFamilyId(firstFamily?.id || '');
      setSelectedRoleId(firstRole?.id || '');
      return domainId;
    });
  };

  const openAddFamilyModal = (domainId) => {
    setCreateModal({ type: 'family', domainId });
  };

  const openAddRoleModal = (domainId, familyId) => {
    setCreateModal({ type: 'role', domainId, familyId });
  };

  const handleTopAddDomain = () => {
    setCreateModal({ type: 'domain' });
  };

  const handleTopAddFamily = () => {
    if (!expandedDomain) {
      toast.info('Open a sector first');
      return;
    }
    openAddFamilyModal(expandedDomain);
  };

  const handleTopAddRole = () => {
    if (!expandedDomain) {
      toast.info('Open a sector first');
      return;
    }
    const domain = domains.find((d) => d.id === expandedDomain);
    if (!domain?.families?.length) {
      toast.info('Add a domain first');
      return;
    }
    if (!selectedFamilyId || !domain.families.some((f) => f.id === selectedFamilyId)) {
      toast.info('Select a domain first');
      return;
    }
    openAddRoleModal(expandedDomain, selectedFamilyId);
  };

  const handleCardAddRole = (domainId) => {
    const domain = domains.find((d) => d.id === domainId);
    if (!domain?.families?.length) {
      toast.info('Add a domain first');
      return;
    }
    const familyId = (domainId === expandedDomain && selectedFamilyId && domain.families.some((f) => f.id === selectedFamilyId))
      ? selectedFamilyId
      : domain.families[0].id;
    openAddRoleModal(domainId, familyId);
  };

  const handleCreateDomain = ({ name }) => {
    const trimmed = name?.trim();
    if (!trimmed) {
      toast.error('Please enter a sector name');
      return;
    }
    const created = addCustomDomain({ name: trimmed });
    if (!created) {
      toast.error('Could not create sector');
      return;
    }
    setCreateModal(null);
    setExpandedDomain(created.id);
    setSelectedFamilyId('');
    setSelectedRoleId('');
    toast.success(`Sector "${created.name}" created`);
  };

  const handleDeleteDomain = (domainId, domainName) => {
    if (!window.confirm(`Delete sector "${domainName}"? All domains and roles under it will be removed.`)) return;
    if (!deleteDomain(domainId)) {
      toast.error('Could not delete sector');
      return;
    }
    if (expandedDomain === domainId) {
      setExpandedDomain(null);
      setSelectedFamilyId('');
      setSelectedRoleId('');
    }
    toast.success(`Sector "${domainName}" deleted`);
  };

  const handleCreateFamily = (domainId, { name }) => {
    const trimmed = name?.trim();
    if (!trimmed) {
      toast.error('Please enter a domain name');
      return;
    }
    const created = addCustomFamily(domainId, { name: trimmed });
    if (!created) {
      toast.error('Could not create domain');
      return;
    }
    setCreateModal(null);
    setExpandedDomain(domainId);
    setSelectedFamilyId(created.id);
    setSelectedRoleId('');
    toast.success(`Domain "${created.name}" created`);
  };

  const handleCreateRole = async (domainId, familyId, { title }) => {
    const trimmed = title?.trim();
    if (!trimmed) {
      toast.error('Please enter a role title');
      return;
    }

    const domain = domains.find((d) => d.id === domainId);
    const family = domain?.families.find((f) => f.id === familyId);
    if (!domain || !family) {
      toast.error('Could not find sector or domain');
      return;
    }

    setGeneratingRoleSkills(true);
    try {
      const created = addCustomRole(domainId, familyId, { title: trimmed });
      if (!created) {
        toast.error('Could not create role');
        return;
      }

      const roleKey = getRoleKey(domainId, familyId, created.id);
      let aiSucceeded = false;

      try {
        const skills = await generateAiRoleSkillWeights({
          roleTitle: created.title,
          sectorName: domain.name,
          domainName: family.name,
          sectorDescription: domain.description || '',
        });
        saveStoredRoleSkills(roleKey, skills);
        window.dispatchEvent(new CustomEvent('reachout:job-family-skills-updated', { detail: { roleKey } }));
        aiSucceeded = true;
      } catch (aiError) {
        console.warn('AI skill weight generation failed:', aiError);
        if (isJobFamilyAiEnabled()) {
          toast.warn('Designation created, but AI could not generate skill weights. Add them manually below.');
        } else {
          // AI is off: start from the default weights so the designation is never left empty.
          const defaults = getDefaultSkillWeightagesForTitle(created.title)
            .map((s) => ({ skill: s.skillName, weight: s.weightage }));
          saveStoredRoleSkills(roleKey, defaults);
          window.dispatchEvent(new CustomEvent('reachout:job-family-skills-updated', { detail: { roleKey } }));
        }
      }

      setCreateModal(null);
      setExpandedDomain(domainId);
      setSelectedFamilyId(familyId);
      setSelectedRoleId(created.id);

      if (aiSucceeded) {
        toast.success(`Designation "${created.title}" created with AI-generated skill weights`);
      }
    } finally {
      setGeneratingRoleSkills(false);
    }
  };

  return (
    <div className="settings-jf">
      <div className="settings-jf-action-bar">
        <AddMenuButton
          label="Add Job Family"
          options={[
            { label: 'Sector', onClick: handleTopAddDomain },
            { label: 'Domain', onClick: handleTopAddFamily },
            { label: 'Designation', onClick: handleTopAddRole },
          ]}
        />
      </div>

      <div className="settings-jf-domains">
        {domains.map((domain) => {
          const isOpen = expandedDomain === domain.id;
          const DomainIcon = DOMAIN_ICONS[domain.id] || Monitor;

          return (
            <div key={domain.id} className={`settings-jf-domain${isOpen ? ' settings-jf-domain--open' : ''}`}>
              <div className="settings-jf-domain-header-wrap">
                <button
                  type="button"
                  className="settings-jf-domain-header"
                  onClick={() => toggleDomain(domain.id)}
                  aria-expanded={isOpen}
                >
                  <div className="settings-jf-domain-header-left">
                    <div className="settings-jf-domain-icon" aria-hidden="true">
                      <DomainIcon size={18} strokeWidth={1.75} />
                    </div>
                    <div className="settings-jf-domain-header-text">
                      <span className="settings-jf-domain-name">{domain.name}</span>
                      <span className="settings-jf-domain-badge">
                        {domain.families.length} {domain.families.length === 1 ? 'domain' : 'domains'}
                      </span>
                    </div>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`settings-jf-chevron${isOpen ? ' settings-jf-chevron--open' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className="settings-jf-domain-delete"
                  onClick={() => handleDeleteDomain(domain.id, domain.name)}
                  aria-label={`Delete ${domain.name}`}
                  title={`Delete ${domain.name}`}
                >
                  <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                </button>
              </div>

              {isOpen && (
                <div className="settings-jf-domain-body">
                  <div className="settings-jf-domain-body-toolbar">
                    {domain.description ? (
                      <p className="settings-jf-domain-desc">{domain.description}</p>
                    ) : (
                      <span />
                    )}
                    <AddMenuButton
                      small
                      align="right"
                      label="Add"
                      options={[
                        { label: 'Domain Name', onClick: () => openAddFamilyModal(domain.id) },
                        { label: 'Designation', onClick: () => handleCardAddRole(domain.id) },
                      ]}
                    />
                  </div>

                  {domain.families.length === 0 ? (
                    <p className="settings-jf-empty-msg">No domains yet. Use the Add button to create a domain.</p>
                  ) : (
                    <>
                      <JobFamilyTripleSelect
                        roleKey={activeRoleKey}
                        lockDomainId={domain.id}
                        hideDomainWhenLocked
                        idPrefix={`jf-${domain.id}`}
                        onRoleKeyChange={(key) => {
                          const { familyId, roleId } = parseRoleKey(key);
                          setSelectedFamilyId(familyId);
                          setSelectedRoleId(roleId);
                        }}
                      />

                      {activeRoleKey && (
                        <EditableSkillEditor roleKey={activeRoleKey} />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {createModal?.type === 'domain' && (
        <CreateItemModal
          title="Add Sector"
          fields={[
            { name: 'name', label: 'Sector Name', placeholder: 'e.g. Renewable Energy', autoFocus: true },
          ]}
          onClose={() => setCreateModal(null)}
          onSubmit={handleCreateDomain}
        />
      )}

      {createModal?.type === 'family' && (
        <CreateItemModal
          title="Add Domain"
          fields={[
            { name: 'name', label: 'Domain Name', placeholder: 'e.g. Solar Engineering', autoFocus: true },
          ]}
          onClose={() => setCreateModal(null)}
          onSubmit={(values) => handleCreateFamily(createModal.domainId, values)}
        />
      )}

      {createModal?.type === 'role' && (
        <CreateItemModal
          title="Add Designation"
          submitting={generatingRoleSkills}
          submitLabel="Create & Generate Skills"
          fields={[
            { name: 'title', label: 'Designation Title', placeholder: 'e.g. Senior Solar Engineer', autoFocus: true },
          ]}
          onClose={() => {
            if (!generatingRoleSkills) setCreateModal(null);
          }}
          onSubmit={(values) => handleCreateRole(createModal.domainId, createModal.familyId, values)}
        />
      )}
    </div>
  );
}
